import type { SetRuleCode } from "./set-rules";
import type { CompetitorType } from "./vocab";

/**
 * Team ties — events whose meets are a TIE of individual rubbers (badminton's
 * team event, table tennis, tennis and squash teams), as opposed to a team that
 * is itself one side of the match (sepak takraw's regu, any doubles, football).
 *
 * A tie is a parent meet and each rubber a child meet (`parent_meet_id`). The
 * predecessor fixed the rubbers per sport in its forms (`MATCH_COUNT` and the
 * singles / doubles positions); they are kept here as data, so OVR creates a
 * team event's rubbers with its ties and the tie is decided by the majority
 * (`teamTieWinner`: dead rubbers need not be played).
 *
 * Every export is prefixed `tie…` / `TIE_…` / `RUBBER_…`: the competition
 * modules share one barrel.
 */

export const RUBBER_KINDS = ["SINGLES", "DOUBLES"] as const;
export type RubberKind = (typeof RUBBER_KINDS)[number];

export interface TieRubber {
	kind: RubberKind;
	/** The rubber's own format, written into its `config` when it is created. */
	best_of: number;
	/** The rubber's own set rule when it differs from the sport's (a tennis doubles match tie-break). */
	rule?: SetRuleCode;
}

export interface TieFormat {
	/** The rubbers in playing order. */
	rubbers: readonly TieRubber[];
	/** Squad members named beside the rubbers' players (squash's reserve) — shown, never a rubber. */
	reserves?: number;
	/**
	 * The most rubbers OF ONE KIND a player may play in a tie (BWF Thomas &
	 * Uber Cup: one singles and one doubles at most — never two singles, never
	 * two doubles). Absent = no limit (the old OVR checked none).
	 */
	per_kind?: number;
}

const S = (best_of: number, rule?: SetRuleCode): TieRubber => ({ kind: "SINGLES", best_of, ...(rule ? { rule } : {}) });
const D = (best_of: number, rule?: SetRuleCode): TieRubber => ({ kind: "DOUBLES", best_of, ...(rule ? { rule } : {}) });

/**
 * By SEAG `sports.code`. The predecessor's orders (WSL-OVR-X team screens):
 *   TTE  S, S, D, S, S — won at 3; each rubber best of 5 games (`dsc-tte-tm`)
 *   TEN  S1, D, S2 — won at 2; best of 3 sets, the doubles' deciding set a
 *        10-point match tie-break (`dsc-ten-tm`, `dsc-ten-do`)
 *   SQU  S, S, S + a reserve — won at 2; best of 5 games (`meet-squ-tm`)
 *   BD   S, D, S, D, S — won at 3; best of 3 games (BWF Thomas / Uber Cup
 *        order) — CONFIRM the SEA Games team event's order. A player plays
 *        one singles and one doubles at most (Thomas & Uber Cup regulations)
 */
export const TIE_FORMATS: Readonly<Record<string, TieFormat>> = {
	BD: { rubbers: [S(3), D(3), S(3), D(3), S(3)], per_kind: 1 },
	TTE: { rubbers: [S(5), S(5), D(5), S(5), S(5)] },
	TEN: { rubbers: [S(3, "TENNIS"), D(3, "TENNIS_MATCH_TIEBREAK"), S(3, "TENNIS")] },
	SQU: { rubbers: [S(5), S(5), S(5)], reserves: 1 },
};

/** The predecessor's and the demo's other codes for the same sports. */
const TIE_SPORT_ALIASES: Readonly<Record<string, string>> = { BDM: "BD", TT: "TTE" };

/**
 * The tie format of an event, or null when its meets are not ties: a TEAM
 * event of a sport that has one, with teams larger than a pair — doubles and
 * mixed doubles are TEAM entries of exactly two, one side of one match.
 */
export function tieFormatFor(
	sportCode: string | null | undefined,
	event: { competitor_type: CompetitorType; team_size_max: number | null },
): TieFormat | null {
	const code = (sportCode ?? "").trim().toUpperCase();
	if (!code || event.competitor_type !== "TEAM") return null;
	const format = TIE_FORMATS[code] ?? TIE_FORMATS[TIE_SPORT_ALIASES[code] ?? ""];
	if (!format) return null;
	return event.team_size_max !== null && event.team_size_max <= 2 ? null : format;
}

/** The rubbers a tie needs to win: a majority. */
export const tieRubbersToWin = (format: TieFormat): number => Math.floor(format.rubbers.length / 2) + 1;

/** Each rubber's kind and its number among rubbers of that kind: Singles 1, Doubles 1, Singles 2 … */
export function tieRubberLabels(format: TieFormat): { kind: RubberKind; n: number }[] {
	const seen: Partial<Record<RubberKind, number>> = {};
	return format.rubbers.map((r) => {
		const n = (seen[r.kind] ?? 0) + 1;
		seen[r.kind] = n;
		return { kind: r.kind, n };
	});
}

/** A rubber's English name, "Singles 1" / "Doubles" (a kind played once has no number) — what OVR names the child meet. */
export function tieRubberNames(format: TieFormat): string[] {
	const labels = tieRubberLabels(format);
	const count = (k: RubberKind) => labels.filter((l) => l.kind === k).length;
	return labels.map((l) => `${l.kind === "SINGLES" ? "Singles" : "Doubles"}${count(l.kind) > 1 ? ` ${l.n}` : ""}`);
}

/** The `config` a rubber is created with: its best-of, and its own rule when it has one. */
export const tieRubberConfig = (r: TieRubber): { best_of: number; rule?: SetRuleCode } => (r.rule ? { best_of: r.best_of, rule: r.rule } : { best_of: r.best_of });

/** Players on one side of a rubber: one for singles, two for doubles. */
export const tieRubberPlayers = (kind: RubberKind): number => (kind === "SINGLES" ? 1 : 2);

/**
 * Each rubber's kind, by playing order, for a tie that holds `count` rubbers
 * — null when the count is not the format's (a tie set up by hand some other
 * way: its kinds are not known, so nothing is checked against them).
 */
export function tieRubberKinds(format: TieFormat, count: number): RubberKind[] | null {
	return count === format.rubbers.length ? format.rubbers.map((r) => r.kind) : null;
}

/**
 * What is wrong with one side's line-up in rubber `index` of a tie (playing
 * order), against the other rubbers' line-ups of the SAME team: too many
 * players for the kind, or a player already in another rubber of that kind
 * beyond `per_kind`. Members are roster ids. Empty = nothing. OVR shows it;
 * the API refuses it (`PUT /meet-competitors/:id/lineup`).
 */
export type TieLineupProblem = { kind: "TOO_MANY"; max: number } | { kind: "SAME_KIND"; member: string; rubber: number };

export function tieLineupProblems(
	format: TieFormat,
	kinds: readonly RubberKind[],
	index: number,
	members: readonly string[],
	others: readonly { index: number; members: readonly string[] }[],
): TieLineupProblem[] {
	const kind = kinds[index];
	if (kind === undefined) return [];
	const out: TieLineupProblem[] = [];
	const max = tieRubberPlayers(kind);
	if (members.length > max) out.push({ kind: "TOO_MANY", max });
	if (format.per_kind !== undefined) {
		for (const member of members) {
			const same = others.filter((o) => o.index !== index && kinds[o.index] === kind && o.members.includes(member));
			const first = same[0];
			if (first && same.length >= format.per_kind) out.push({ kind: "SAME_KIND", member, rubber: first.index });
		}
	}
	return out;
}
